import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Eye, Edit, Trash2, User, Clock, Activity, Link2 as LinkIcon } from 'lucide-react';
import toast from 'react-hot-toast';
import { jobApplications, statuses, users, libraries, jobs } from '../services/apiService';
import DateRangePicker from '../components/UI/DateRangePicker';
import TalentFormModal from '../components/UI/TalentFormModal';
import SearchInput from '../components/UI/SearchInput';
import ConfirmDialog from '../components/UI/ConfirmDialog';
import { Table, Pagination, Autocomplete } from '@anyemp/ui-kit';
import { PAGINATION_OPTIONS } from '../utils/paginationConstants';
import { loadColumnsStateFromLS, useTableColumns } from '../utils/tableColumnsPersistence';
import { getToolIcon } from '../utils/toolIconUtils.jsx';
import StatusSelect from '../components/UI/StatusSelect';
import ManagerSelect from '../components/UI/ManagerSelect';
import ProfileLink from '../components/UI/ProfileLink';
import StatusTransitionModal from '../components/StatusManagement/StatusTransitionModal';
import HistoryModal from '../components/UI/HistoryModal';
import { formatApiError } from '../utils/formatApiError';
import Avatar from '../components/UI/Avatar';
import ActivityFitPanel from '../components/UI/ActivityFitPanel';
import SavedFiltersDropdown from '../components/SavedFilters/SavedFiltersDropdown';
import { PAGE_KEYS } from '../constants/savedFilterPageKeys';
import useResponsiveViewPreference from '../hooks/useResponsiveViewPreference';
import useLatestRequest from '../hooks/useLatestRequest';
import DataViewToggle from '../components/UI/DataViewToggle';
import { RecordCard, RecordCardField, RecordCardFields, RecordCardHeader, ResponsiveCardGrid } from '../components/UI/ResponsiveRecordCards';
import CompactCardSort from '../components/Talent/CompactCardSort';
import { JOB_APPLICATION_CARD_SORT_OPTIONS } from '../utils/cardSortOptions';
import {
  DEFAULT_JOB_APPLICATION_SORT,
  getJobApplicationColumnKey,
  getJobApplicationSortField,
  normalizeJobApplicationSort,
} from '../utils/jobApplicationListState';

const SAVED_FILTER_KEYS = [
  'search', 'status_id', 'manager_id', 'gender',
  'country_id', 'city_id', 'tool_id', 'link_tool_id', 'communication_manager_id',
  'profession_id', 'department_id',
  'created_from', 'created_to', 'updated_from', 'updated_to',
];

const BASE_TABLE_COLUMNS = [
  { key: 'name', label: 'Name', sortable: true, alwaysPinned: true, alwaysVisible: true },
  { key: 'id', label: 'ID', sortable: true },
  { key: 'manager', label: 'Manager', sortable: true },
  { key: 'gender', label: 'Gender', sortable: true },
  { key: 'age', label: 'Age', sortable: true },
  { key: 'professions', label: 'Professions', sortable: false },
  { key: 'location', label: 'Location', sortable: true },
  { key: 'jobPost', label: 'Job Post', sortable: false },
  { key: 'status', label: 'Status', sortable: true },
  { key: 'contacts', label: 'Contacts', sortable: false },
  { key: 'links', label: 'Links', sortable: false },
  { key: 'lastCommunication', label: 'Last Communication', sortable: false },
  { key: 'created', label: 'Created', sortable: true },
  { key: 'createdBy', label: 'Created By', sortable: false },
  { key: 'history', label: 'History', sortable: false },
  { key: 'actions', label: 'Actions', sortable: false },
];

const JobApplications = () => {
  const location = useLocation()
  const [isActivityFitOpen, setIsActivityFitOpen] = useState(false);
  const [applications, setApplications] = useState([]);
  const [tableLoading, setTableLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    current_page: 1,
    total_pages: 1,
    total_items: 0,
    items_per_page: 10
  });
  const [filters, setFilters] = useState(() => {
    const base = {
      search: '',
      status_id: '',
      manager_id: '',
      gender: '',
      country_id: '',
      city_id: '',
      tool_id: '',
      link_tool_id: '',
      communication_manager_id: '',
      profession_id: '',
      department_id: '',
      job_post_id: '',
      created_from: '',
      created_to: '',
      updated_from: '',
      updated_to: '',
      page: 1,
      limit: 10,
      ...DEFAULT_JOB_APPLICATION_SORT,
      includes: 'professions,contacts,links,manager,country,city,status,communications'
    };
    // Restore sort + current page across navigation (column visibility is in localStorage)
    try {
      const saved = JSON.parse(sessionStorage.getItem('jobApplications_tableState') || '{}');
      if (saved && typeof saved === 'object') {
        if (typeof saved.sort_by === 'string') base.sort_by = saved.sort_by;
        if (saved.sort_order === 'ASC' || saved.sort_order === 'DESC') base.sort_order = saved.sort_order;
        if (Number.isInteger(saved.page) && saved.page > 0) base.page = saved.page;
      }
    } catch {}
    Object.assign(base, normalizeJobApplicationSort(base.sort_by, base.sort_order));
    return base;
  });
  const [expandedContacts, setExpandedContacts] = useState(new Set());
  const [expandedProfessions, setExpandedProfessions] = useState(new Set());
  const [statusOptions, setStatusOptions] = useState([]);
  const [userOptions, setUserOptions] = useState([]);
  const [countryOptions, setCountryOptions] = useState([]);
  const [cityOptions, setCityOptions] = useState([]);
  const [toolsOptions, setToolsOptions] = useState([]);
  const [linkToolsOptions, setLinkToolsOptions] = useState([]);
  const [professionsOptions, setProfessionsOptions] = useState([]);
  const [departmentsOptions, setDepartmentsOptions] = useState([]);
  const [jobPostOptions, setJobPostOptions] = useState([]);

  const [filtersExpanded, setFiltersExpanded] = useState(false);
  const { mode: viewMode, selectMode: setViewMode } = useResponsiveViewPreference('job-applications');

  // State for conversion modal
  const [convertModalOpen, setConvertModalOpen] = useState(false);
  const [selectedApplication, setSelectedApplication] = useState(null);

  // State for delete confirmation
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // State for status editing
  const [editingStatusId, setEditingStatusId] = useState(null);
  const [statusChangeLoading, setStatusChangeLoading] = useState(null);

  // State for manager editing
  const [editingManagerId, setEditingManagerId] = useState(null);
  const [managerChangeLoading, setManagerChangeLoading] = useState(null);

  // Status transition modal state
  const [showTransitionModal, setShowTransitionModal] = useState(false);
  const [transitionData, setTransitionData] = useState(null);

  // History modal state
  const [historyModalOpen, setHistoryModalOpen] = useState(false);
  const [selectedApplicationForHistory, setSelectedApplicationForHistory] = useState(null);

  // Stable handlers for Table
  const rowKey = useCallback((application) => application.id, []);

  // Sorting object for the Table component
  const sortingInfo = useMemo(() => ({
    sort_by: getJobApplicationColumnKey(filters.sort_by),
    sort_order: filters.sort_order
  }), [filters.sort_by, filters.sort_order]);

  const tableColumns = useMemo(
    () => BASE_TABLE_COLUMNS.map((col) => ({
      ...col,
      render: col.key === 'actions'
        ? (application) => renderTableActions(application)
        : (application) => renderTableCell(col, application),
    })),
    // baseTableColumns is a stable literal; renderTable* close over the
    // applications array via component scope, so include `applications`.
    [applications, expandedContacts, expandedProfessions, editingStatusId, statusChangeLoading, editingManagerId, managerChangeLoading, userOptions]
  );

  const tableColumnsState = useTableColumns(tableColumns, {
    ...(() => {
      const loaded = loadColumnsStateFromLS(
        'jobApplications_columns',
        { defaultPinned: ['name'], defaultHidden: ['createdBy'] },
        'jobApplications_visibleColumns'
      );
      // ID — колонка, добавленная позже: в сохранённом порядке её нет, и таблица
      // поставила бы её в самый конец. Name закреплён и в сохранённый порядок не
      // попадает, поэтому ставим ID первым среди остальных — сразу после имени
      const order = loaded.defaultOrder;
      return order.length > 0 && !order.includes('id')
        ? { ...loaded, defaultOrder: ['id', ...order] }
        : loaded;
    })(),
    onChange: (state) => {
      try { localStorage.setItem('jobApplications_columns', JSON.stringify(state)); } catch {}
    },
  });

  // Функция для рендеринга ячеек таблицы
  const renderTableCell = (column, application, columnIndex) => {
    switch (column.key) {
      case 'name':
        return (
          <ProfileLink
            to={`/job-applications/${application.id}`}
            ariaLabel={`View application of ${application.name}`}
          >
            {application.name}
          </ProfileLink>
        );

      case 'id':
        return (
          <div className="text-sm text-light-text-primary dark:text-dark-text-primary font-mono">
            {application.id}
          </div>
        );

      case 'gender':
        return (
          <div className="text-sm text-light-text-primary dark:text-dark-text-primary capitalize">
            {application.gender || '-'}
          </div>
        );

      case 'age':
        return (
          <div className="text-sm text-light-text-primary dark:text-dark-text-primary">
            {application.age || '-'}
          </div>
        );

      case 'professions':
        if (!application.professions || application.professions.length === 0) {
          return <span className="text-light-text-tertiary dark:text-dark-text-tertiary text-sm">No professions</span>;
        }

        const isProfessionsExpanded = expandedProfessions.has(application.id);
        const displayProfessions = isProfessionsExpanded ? application.professions : application.professions.slice(0, 2);

        return (
          <div className="text-sm">
            <div className="flex flex-wrap gap-1">
              {displayProfessions.map((profession, index) => (
                  <span
                    key={profession.id || index}
                    className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-light-bg-tertiary dark:bg-dark-bg-tertiary text-light-text-secondary dark:text-dark-text-secondary"
                    title={profession.department?.name ? `${profession.department.name} - ${profession.profession?.name || profession.profession_name}` : (profession.profession?.name || profession.profession_name)}
                  >
                    {profession.profession?.name || profession.profession_name}
                  </span>
              ))}
            </div>
            {application.professions.length > 2 && (
              <div className="mt-1">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleProfessionsExpansion(application.id);
                  }}
                  className={`inline-flex items-center px-2 py-1 text-xs font-medium rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-offset-1 ${
                    isProfessionsExpanded
                      ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 hover:bg-blue-200 dark:hover:bg-blue-900/50 focus:ring-blue-500 dark:focus:ring-blue-400'
                      : 'bg-light-bg-tertiary dark:bg-dark-bg-tertiary text-light-text-secondary dark:text-dark-text-secondary hover:bg-light-bg-quaternary dark:hover:bg-dark-bg-quaternary focus:ring-gray-500 dark:focus:ring-gray-400'
                  }`}
                >
                  {isProfessionsExpanded ? (
                    <>
                      <span>Hide</span>
                      <svg className="ml-1 h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                      </svg>
                    </>
                  ) : (
                    <>
                      <span>+{application.professions.length - 2}</span>
                      <svg className="ml-1 h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </>
                  )}
                </button>
              </div>
            )}
          </div>
        );

      case 'location':
        return (
          <div className="text-sm text-light-text-primary dark:text-dark-text-primary">
            {application.country ? (
              <div>
                <div className="font-medium">{application.country.name}</div>
                {application.city && (
                  <div className="text-xs text-light-text-tertiary dark:text-dark-text-tertiary">{application.city.name}</div>
                )}
              </div>
            ) : (
              '-'
            )}
          </div>
        );

      case 'jobPost':
        return (
          <div className="text-sm text-light-text-primary dark:text-dark-text-primary">
            {application.job_post ? (
              <span title={application.job_post.name}>{application.job_post.name}</span>
            ) : application.job_post_id ? (
              <span className="text-light-text-tertiary dark:text-dark-text-tertiary italic">[Deleted Post]</span>
            ) : (
              '-'
            )}
          </div>
        );

      case 'status':
        return (
          <StatusSelect
            currentStatus={application.status}
            statusOptions={statusOptions}
            isEditing={editingStatusId === application.id}
            isLoading={statusChangeLoading === application.id}
            onEdit={() => handleStatusEdit(application.id)}
            onCancel={handleStatusCancel}
            onChange={(statusId) => handleStatusChange(application.id, statusId)}
            emptyLabel="No Status"
          />
        );

      case 'contacts':
        if (!application.contacts || application.contacts.length === 0) {
          return <span className="text-sm text-light-text-tertiary dark:text-dark-text-tertiary">No contacts</span>;
        }

        const isExpanded = expandedContacts.has(application.id);
        const displayContacts = isExpanded ? application.contacts : application.contacts.slice(0, 2);

        return (
          <div className="text-sm">
            <div className="space-y-1">
              {displayContacts.map((contact) => {
                const contactValue = typeof contact.value === 'object' ? contact.value?.value || 'Not specified' : contact.value || 'Not specified';

                return (
                  <div key={contact.id} className="flex items-center space-x-2">
                    {contact.tool && (
                      <span
                        className="inline-flex items-center cursor-help"
                        title={contact.tool.name}
                      >
                        {getToolIcon(contact.tool, { size: 'h-5 w-5' })}
                      </span>
                    )}
                    <div className="flex-1 min-w-0 flex items-center space-x-1">
                      <div
                        className="text-sm text-light-text-primary dark:text-dark-text-primary truncate max-w-[200px] cursor-pointer hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                        onClick={() => copyContactToClipboard(contactValue)}
                        title={`Click to copy: ${contactValue}`}
                      >
                        {contactValue}
                      </div>
                      {contact.is_primary && (
                        <span className="ml-2 px-1 py-0.5 bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 text-xs rounded flex-shrink-0">
                          Primary
                        </span>
                      )}
                      {contact.is_verified && (
                        <div className="text-green-500 dark:text-green-400 text-xs flex-shrink-0">✓</div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
            {application.contacts.length > 2 && (
              <div className="mt-1">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleContactsExpansion(application.id);
                  }}
                  className={`inline-flex items-center px-2 py-1 text-xs font-medium rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-offset-1 ${
                    isExpanded
                      ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 hover:bg-blue-200 dark:hover:bg-blue-900/50 focus:ring-blue-500 dark:focus:ring-blue-400'
                      : 'bg-light-bg-tertiary dark:bg-dark-bg-tertiary text-light-text-secondary dark:text-dark-text-secondary hover:bg-light-bg-quaternary dark:hover:bg-dark-bg-quaternary focus:ring-gray-500 dark:focus:ring-gray-400'
                  }`}
                >
                  {isExpanded ? (
                    <>
                      <span>Hide</span>
                      <svg className="ml-1 h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                      </svg>
                    </>
                  ) : (
                    <>
                      <span>+{application.contacts.length - 2}</span>
                      <svg className="ml-1 h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </>
                  )}
                </button>
              </div>
            )}
          </div>
        );

      case 'links':
        if (!application.links || application.links.length === 0) {
          return <span className="text-sm text-light-text-tertiary dark:text-dark-text-tertiary">No links</span>;
        }
        return (
          <div className="flex flex-wrap items-center gap-2">
            {application.links.map((link) => (
              <a
                key={link.id}
                href={link.value}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="inline-flex items-center text-light-text-secondary dark:text-dark-text-secondary hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                title={`${link.tool_name || link.tool?.name || 'Link'}: ${link.value}`}
              >
                {link.tool
                  ? getToolIcon(link.tool, { size: 'h-5 w-5' })
                  : <LinkIcon className="h-5 w-5" />}
              </a>
            ))}
          </div>
        );

      case 'lastCommunication':
        return formatLastCommunication(application.communications);

      case 'manager':
        const manager = userOptions.find(u => u.id === application.manager_id);
        return (
          <ManagerSelect
            currentManager={manager || application.manager}
            currentManagerId={application.manager_id}
            managerOptions={userOptions}
            isEditing={editingManagerId === application.id}
            isLoading={managerChangeLoading === application.id}
            onEdit={() => handleManagerEdit(application.id)}
            onCancel={handleManagerCancel}
            onChange={(managerId) => handleManagerChange(application.id, managerId)}
          />
        );

      case 'history':
        return application.events && application.events.length > 0 ? (
          <button
            onClick={() => handleHistoryClick(application)}
            className="text-left w-full hover:bg-light-bg-tertiary dark:hover:bg-dark-bg-tertiary p-2 rounded transition-colors group"
          >
            <div className="text-sm text-light-text-primary dark:text-dark-text-primary">
              <div className="flex items-center">
                <Clock className="h-4 w-4 text-light-text-tertiary dark:text-dark-text-tertiary mr-2 group-hover:text-blue-600 dark:group-hover:text-blue-400" />
                <span className="group-hover:text-blue-600 dark:group-hover:text-blue-400 font-medium">
                  {application.events.length} events
                </span>
              </div>
              <div className="text-xs text-light-text-secondary dark:text-dark-text-secondary mt-1 group-hover:text-blue-600 dark:group-hover:text-blue-400">
                Last: {formatDate(application.events[0]?.occurred_at)}
              </div>
            </div>
          </button>
        ) : (
          <span className="text-sm text-light-text-tertiary dark:text-dark-text-tertiary">No events</span>
        );

      case 'created':
        return (
          <div className="text-sm text-light-text-primary dark:text-dark-text-primary">
            {formatDate(application.created_at)}
          </div>
        );

      case 'createdBy':
        return application.createdByUser ? (
          <div className="flex items-center space-x-2">
            <Avatar
              avatar={application.createdByUser.avatar}
              name={application.createdByUser.fullName || application.createdByUser.name}
              email={application.createdByUser.email}
              size="xs"
            />
            <span className="text-sm text-light-text-primary dark:text-dark-text-primary">
              {application.createdByUser.fullName || application.createdByUser.name || application.createdByUser.email}
            </span>
          </div>
        ) : (
          <span className="text-sm text-light-text-tertiary dark:text-dark-text-tertiary">-</span>
        );

      default:
        return <span className="text-sm text-light-text-primary dark:text-dark-text-primary">-</span>;
    }
  };

  // Функция для рендеринга действий
  const renderTableActions = (application) => {
    // Проверяем, есть ли связанный талант
    const hasTalent = application.talent && application.talent.id;
    
    return (
      <div className="flex min-w-0 flex-col gap-2">
        {/* Main actions */}
        <div className="flex flex-wrap items-center justify-center gap-2">
          <Link
            to={`/job-applications/${application.id}`}
            className="text-blue-600 dark:text-blue-400 hover:text-blue-900 dark:hover:text-blue-300"
            title="View"
          >
            <Eye className="h-4 w-4" />
          </Link>
          <Link
            to={`/job-applications/${application.id}/edit`}
            className="text-indigo-600 dark:text-indigo-400 hover:text-indigo-900 dark:hover:text-indigo-300"
            title="Edit"
          >
            <Edit className="h-4 w-4" />
          </Link>
          <button
            onClick={() => setDeleteTarget(application)}
            className="text-red-600 dark:text-red-400 hover:text-red-900 dark:hover:text-red-300"
            title="Delete"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>

        {/* Conditional display: conversion buttons or talent profile link */}
        {hasTalent ? (
          // If there's a linked talent, show profile link
          <div className="flex flex-wrap items-center justify-center gap-1">
            <Link
              to={`/talents/${application.talent.id}`}
              state={{ from: location.pathname + location.search }}
              className="px-2 py-1 text-xs bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300 rounded hover:bg-green-200 dark:hover:bg-green-900/50 transition-colors flex items-center gap-1"
              title={`View Talent Profile (${application.talent.talent_type})`}
            >
              <User className="h-3 w-3" />
              View Talent Profile
            </Link>
          </div>
        ) : (
          // If no linked talent, show conversion buttons
          <div className="flex flex-wrap items-center justify-center gap-1">
            <button
              onClick={() => openConvertModal(application, 'candidate')}
              className="px-2 py-1 text-xs bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 rounded hover:bg-blue-200 dark:hover:bg-blue-900/50 transition-colors"
              title="Convert to Candidate"
            >
              → Candidate
            </button>
            <button
              onClick={() => openConvertModal(application, 'employee')}
              className="px-2 py-1 text-xs bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300 rounded hover:bg-green-200 dark:hover:bg-green-900/50 transition-colors"
              title="Convert to Employee"
            >
              → Employee
            </button>
            <button
              onClick={() => openConvertModal(application, 'presale')}
              className="px-2 py-1 text-xs bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-300 rounded hover:bg-purple-200 dark:hover:bg-purple-900/50 transition-colors"
              title="Convert to Presale"
            >
              → Presale
            </button>
          </div>
        )}
      </div>
    );
  };

  useEffect(() => {
    fetchApplications(false);
  }, [filters]);

  // Persist sort + current page so they survive navigation away and back
  useEffect(() => {
    try {
      sessionStorage.setItem('jobApplications_tableState', JSON.stringify({
        sort_by: filters.sort_by,
        sort_order: filters.sort_order,
        page: filters.page
      }));
    } catch {}
  }, [filters.sort_by, filters.sort_order, filters.page]);

  useEffect(() => {
    fetchReferenceData();
  }, []);

  // Загружаем состояние фильтров из localStorage при инициализации
  useEffect(() => {
    const savedFiltersExpanded = localStorage.getItem('jobApplications_filtersExpanded');
    if (savedFiltersExpanded !== null) {
      setFiltersExpanded(JSON.parse(savedFiltersExpanded));
    }
  }, []);

  // Only the latest request may write the table (filters can change faster
  // than the server answers)
  const startListRequest = useLatestRequest();

  const fetchApplications = async (isInitialLoad = false) => {
    const isCurrent = startListRequest();
    try {
      setTableLoading(true);
      setError(null);

      // Build params from filters, filtering out empty values (align with Talents)
      const params = {
        ...Object.fromEntries(
          Object.entries(filters).filter(([_, value]) => value !== '' && value !== null && value !== undefined)
        )
      };

      const response = await jobApplications.getJobApplications(params);
      if (!isCurrent()) return;

      if (response.success) {
        setApplications(response.data);
        setPagination(response.pagination);
      } else {
        setError('Failed to fetch applications');
      }
    } catch (err) {
      if (!isCurrent()) return;
      console.error('Error fetching applications:', err);
      setError('Error loading applications');
    } finally {
      if (isCurrent()) setTableLoading(false);
    }
  };

  const fetchReferenceData = async () => {
    try {
      console.log('🔄 Loading reference data...');
      
      // Загружаем статусы для типа job_application
      console.log('📊 Loading statuses for job_application type...');
      const statusResponse = await statuses.getStatusesByType('job_application', { isCommon: 1 });
      console.log('📊 Statuses response:', statusResponse);
      if (statusResponse.success) {
        // По алфавиту — как в списке талантов, чтобы порядок статусов был одинаковый везде
        const statusData = (Array.isArray(statusResponse.data) ? statusResponse.data : [])
          .slice()
          .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
        setStatusOptions(statusData);
        console.log('✅ Statuses loaded for job_application:', statusData.length);
      }

      // Загружаем пользователей
      console.log('👥 Loading users...');
      const userResponse = await users.getAllUsers();
      console.log('👥 Users response:', userResponse);
      if (userResponse.success) {
        // Проверяем структуру данных пользователей
        const usersData = Array.isArray(userResponse.data) ? userResponse.data : userResponse.data.users || [];
        setUserOptions(usersData);
        console.log('✅ Users loaded:', usersData.length);
      }

      // Загружаем страны
      console.log('🌍 Loading countries...');
      const countryResponse = await libraries.getCountries();
      console.log('🌍 Countries response:', countryResponse);
      if (countryResponse.success) {
        const countryData = Array.isArray(countryResponse.data) ? countryResponse.data : [];
        setCountryOptions(countryData);
        console.log('✅ Countries loaded:', countryData.length);
      }

      // Загружаем города
      console.log('🏙️ Loading cities...');
      const cityResponse = await libraries.getAllCities();
      console.log('🏙️ Cities response:', cityResponse);
      if (cityResponse.success) {
        const cityData = Array.isArray(cityResponse.data) ? cityResponse.data : [];
        setCityOptions(cityData);
        console.log('✅ Cities loaded:', cityData.length);
      }

      // Загружаем инструменты
      console.log('🔧 Loading tools...');
      const toolsResponse = await libraries.getContactTools();
      console.log('🔧 Tools response:', toolsResponse);
      if (toolsResponse.success) {
        const toolsData = Array.isArray(toolsResponse.data) ? toolsResponse.data : [];
        setToolsOptions(toolsData);
        console.log('✅ Tools loaded:', toolsData.length);
      }

      // Загружаем инструменты для ссылок
      console.log('🔗 Loading link tools...');
      const linkToolsResponse = await libraries.getLinkTools();
      console.log('🔗 Link tools response:', linkToolsResponse);
      if (linkToolsResponse.success) {
        const linkToolsData = Array.isArray(linkToolsResponse.data) ? linkToolsResponse.data : [];
        setLinkToolsOptions(linkToolsData);
        console.log('✅ Link tools loaded:', linkToolsData.length);
      }

      // Загружаем профессии
      console.log('💼 Loading professions...');
      const professionsResponse = await libraries.getProfessions();
      console.log('💼 Professions response:', professionsResponse);
      if (professionsResponse.success) {
        const professionsData = Array.isArray(professionsResponse.data) ? professionsResponse.data : [];
        setProfessionsOptions(professionsData);
        console.log('✅ Professions loaded:', professionsData.length);
      }

      // Загружаем департаменты
      console.log('🏢 Loading departments...');
      const departmentsResponse = await libraries.getDepartments();
      console.log('🏢 Departments response:', departmentsResponse);
      if (departmentsResponse.success) {
        const departmentsData = Array.isArray(departmentsResponse.data) ? departmentsResponse.data : [];
        setDepartmentsOptions(departmentsData);
        console.log('✅ Departments loaded:', departmentsData.length);
      }

      // Загружаем job-посты (short=true, all=true) для фильтра
      try {
        const jobPostsResponse = await jobs.listPosts();
        const payload = jobPostsResponse?.data;
        let items = [];
        if (Array.isArray(payload)) items = payload;
        else if (Array.isArray(payload?.data)) items = payload.data;
        else if (Array.isArray(payload?.rows)) items = payload.rows;
        else if (Array.isArray(payload?.items)) items = payload.items;
        setJobPostOptions(items);
        console.log('✅ Job posts loaded:', items.length);
      } catch (jpErr) {
        console.error('❌ Error fetching job posts:', jpErr);
      }

      console.log('🎉 All reference data loaded successfully!');
    } catch (err) {
      console.error('❌ Error fetching reference data:', err);
    }
  };

  const handleFilterChange = (key, value) => {
    console.log('JobApplications: Filter changed:', key, value);
    setFilters(prev => ({
      ...prev,
      [key]: value,
      page: 1 // Reset to first page when filtering
    }));

  };

  const handleResetFilters = () => {
    setFilters({
      search: '',
      status_id: '',
      manager_id: '',
      gender: '',
      country_id: '',
      city_id: '',
      tool_id: '',
      link_tool_id: '',
      communication_manager_id: '',
      profession_id: '',
      department_id: '',
      job_post_id: '',
      created_from: '',
      created_to: '',
      updated_from: '',
      updated_to: '',
      page: 1,
      limit: 10,
      ...DEFAULT_JOB_APPLICATION_SORT,
      includes: 'professions,contacts,links,manager,country,city,status,communications'
    });
  };

  const handlePageChange = (page) => {
    setFilters(prev => ({
      ...prev,
      page
    }));
  };

  const handleSort = (column) => {
    setFilters(prev => {
      let newSortOrder = 'ASC';

      // Если кликаем по той же колонке, меняем порядок сортировки
      if (prev.sort_by === getJobApplicationSortField(column)) {
        newSortOrder = prev.sort_order === 'ASC' ? 'DESC' : 'ASC';
      }

      return {
        ...prev,
        sort_by: getJobApplicationSortField(column),
        sort_order: newSortOrder,
        page: 1 // Reset to first page when sorting
      };
    });

  };

  const openConvertModal = (application, talentType = 'candidate') => {
    setSelectedApplication({ ...application, talentType });
    setConvertModalOpen(true);
  };

  const closeConvertModal = () => {
    setConvertModalOpen(false);
    setSelectedApplication(null);
  };

  const handleDelete = async (id) => {
    setDeleting(true);
    try {
      setTableLoading(true);
      await jobApplications.deleteJobApplication(id);
      setApplications(prev => prev.filter(app => app.id !== id));
      // После успешного удаления обновляем список
      await fetchApplications(false);
    } catch (err) {
      console.error('Error deleting application:', err);
      toast.error('Error deleting application');
    } finally {
      setDeleting(false);
      setTableLoading(false);
    }
  };

  // Status editing handlers
  const handleStatusEdit = (applicationId) => {
    setEditingStatusId(applicationId);
  };

  const handleStatusCancel = () => {
    setEditingStatusId(null);
    setStatusChangeLoading(null);
  };

  const handleStatusChange = async (applicationId, statusId) => {
    if (!statusId || statusId === '' || statusId === '0') {
      handleStatusCancel();
      return;
    }

    // Find the application and its current status
    const application = applications.find(app => app.id === applicationId);
    if (!application) return;

    const currentStatus = application.status || { id: null, name: 'No status', color: '#6B7280' };

    // If selecting the same status as current → cancel edit (normalize to string)
    const currentIdStr = currentStatus?.id != null ? String(currentStatus.id) : '';
    if (String(statusId) === currentIdStr) {
      handleStatusCancel();
      return;
    }

    const newStatus = statusOptions.find(s => String(s.id) === String(statusId));
    if (!newStatus) {
      toast.error('Selected status is unavailable');
      handleStatusCancel();
      return;
    }

    // Set loading state
    setStatusChangeLoading(applicationId);

    // Open modal to check transition requirements
    setTransitionData({
      talentId: applicationId,
      talentName: application.name,
      fromStatus: { id: currentStatus.id, name: currentStatus.name, color: currentStatus.color },
      toStatus: { id: newStatus.id, name: newStatus.name, color: newStatus.color }
    });
    setShowTransitionModal(true);
  };

  const handleTransitionSuccess = (newApplicationData) => {
    // Update application data
    setApplications(prev => prev.map(app => {
      if (app.id === transitionData.talentId) {
        return {
          ...app,
          status: transitionData.toStatus,
          ...newApplicationData
        };
      }
      return app;
    }));

    // Show success message
    toast.success(`Status changed from "${transitionData.fromStatus.name}" to "${transitionData.toStatus.name}"`);

    // Close modal and editing state
    setShowTransitionModal(false);
    setTransitionData(null);
    setEditingStatusId(null);
    setStatusChangeLoading(null);
  };

  const handleTransitionError = (error) => {
    console.error('Status transition error:', error);

    const errorMessage = formatApiError(error, error.message || 'Error changing status');
    toast.error(errorMessage);

    // Clear all related states and close modal to reset flow
    setShowTransitionModal(false);
    setTransitionData(null);
    setStatusChangeLoading(null);
    setEditingStatusId(null);
  };

  // Manager editing handlers
  const handleManagerEdit = (applicationId) => {
    setEditingManagerId(applicationId);
  };

  const handleManagerCancel = () => {
    setEditingManagerId(null);
    setManagerChangeLoading(null);
  };

  const handleManagerChange = async (applicationId, managerId) => {
    // If empty string, treat as removing manager (set to null)
    const newManagerId = managerId === '' ? null : managerId;

    try {
      setManagerChangeLoading(applicationId);

      // Update job application manager using dedicated endpoint
      await jobApplications.updateJobApplicationManager(applicationId, newManagerId);

      // Update the applications list with the new manager
      setApplications(prev => prev.map(app => {
        if (app.id === applicationId) {
          return { ...app, manager_id: newManagerId };
        }
        return app;
      }));

      setEditingManagerId(null);
      toast.success('Manager updated successfully', {
        duration: 2000,
        position: 'bottom-right',
      });
    } catch (err) {
      console.error('Error updating manager:', err);
      toast.error('Failed to update manager', {
        duration: 2000,
        position: 'bottom-right',
      });
    } finally {
      setManagerChangeLoading(null);
    }
  };

  const toggleContactsExpansion = (applicationId) => {
    setExpandedContacts(prev => {
      const newSet = new Set(prev);
      if (newSet.has(applicationId)) {
        newSet.delete(applicationId);
      } else {
        newSet.add(applicationId);
      }
      return newSet;
    });
  };

  const toggleProfessionsExpansion = (applicationId) => {
    setExpandedProfessions(prev => {
      const newSet = new Set(prev);
      if (newSet.has(applicationId)) {
        newSet.delete(applicationId);
      } else {
        newSet.add(applicationId);
      }
      return newSet;
    });
  };

  const handleHistoryClick = (application) => {
    setSelectedApplicationForHistory(application);
    setHistoryModalOpen(true);
  };

  // Функция копирования контакта в буфер обмена
  const copyContactToClipboard = async (contactValue) => {
    try {
      await navigator.clipboard.writeText(contactValue);
      toast.success(`Copied: ${contactValue}`, {
        duration: 2000,
        position: 'bottom-right',
      });
    } catch (err) {
      console.error('Failed to copy contact:', err);
      // Fallback для старых браузеров
      const textArea = document.createElement('textarea');
      textArea.value = contactValue;
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand('copy');
        toast.success(`Copied: ${contactValue}`, {
          duration: 2000,
          position: 'bottom-right',
        });
      } catch (fallbackErr) {
        console.error('Fallback copy failed:', fallbackErr);
        toast.error('Failed to copy contact', {
          duration: 2000,
          position: 'bottom-right',
        });
      }
      document.body.removeChild(textArea);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Not specified';
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return 'Invalid date';
    }
  };

  const formatContacts = (contacts, applicationId) => {
    if (!contacts || contacts.length === 0) {
      return <span className="text-sm text-light-text-tertiary dark:text-dark-text-tertiary">No contacts</span>;
    }

    const isExpanded = expandedContacts.has(applicationId);
    const displayContacts = isExpanded ? contacts : contacts.slice(0, 2);

    return (
      <div className="text-sm">
        <div className="space-y-1">
          {displayContacts.map((contact) => {
            const contactValue = typeof contact.value === 'object' ? contact.value?.value || 'Not specified' : contact.value || 'Not specified';

            return (
              <div key={contact.id} className="flex items-center space-x-2">
                {contact.tool && (
                  <span
                    className="inline-flex items-center cursor-help"
                    title={contact.tool.name}
                  >
                    {getToolIcon(contact.tool)}
                  </span>
                )}
                <div className="flex-1 min-w-0 flex items-center space-x-1">
                  <div
                    className="text-sm text-light-text-primary dark:text-dark-text-primary truncate max-w-[200px] cursor-pointer hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                    onClick={() => copyContactToClipboard(contactValue)}
                    title={`Click to copy: ${contactValue}`}
                  >
                    {contactValue}
                  </div>
                  {contact.is_primary && (
                    <span className="ml-2 px-1 py-0.5 bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 text-xs rounded flex-shrink-0">
                      Primary
                    </span>
                  )}
                  {contact.is_verified && (
                    <div className="text-green-500 dark:text-green-400 text-xs flex-shrink-0">✓</div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
        {contacts.length > 2 && (
          <div className="mt-1">
            <button
              onClick={(e) => {
                e.stopPropagation();
                toggleContactsExpansion(applicationId);
              }}
              className={`inline-flex items-center px-2 py-1 text-xs font-medium rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-offset-1 ${
                isExpanded
                  ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 hover:bg-blue-200 dark:hover:bg-blue-900/50 focus:ring-blue-500 dark:focus:ring-blue-400'
                  : 'bg-light-bg-tertiary dark:bg-dark-bg-tertiary text-light-text-secondary dark:text-dark-text-secondary hover:bg-light-bg-quaternary dark:hover:bg-dark-bg-quaternary focus:ring-gray-500 dark:focus:ring-gray-400'
              }`}
            >
              {isExpanded ? (
                <>
                  <span>Hide</span>
                  <svg className="ml-1 h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                  </svg>
                </>
              ) : (
                <>
                  <span>+{contacts.length - 2}</span>
                  <svg className="ml-1 h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </>
              )}
            </button>
          </div>
        )}
      </div>
    );
  };

  const formatLastCommunication = (communications) => {
    if (!communications || communications.length === 0) {
      return <span className="text-light-text-tertiary dark:text-dark-text-tertiary text-sm">No communications</span>;
    }

    const lastComm = communications[0]; // Server returns only the last communication by follow_up_date
    const date = new Date(lastComm.created_at).toLocaleDateString();
    
    return (
      <div className="text-sm">
        <div className="text-light-text-primary dark:text-dark-text-primary font-medium truncate max-w-xs" title={lastComm.notes}>
          {lastComm.notes ? lastComm.notes.substring(0, 50) + (lastComm.notes.length > 50 ? '...' : '') : 'No notes'}
        </div>
        <div className="text-light-text-tertiary dark:text-dark-text-tertiary text-xs">
          {date} by {lastComm.manager ? lastComm.manager.name : lastComm.manager_id}
        </div>
        {lastComm.follow_up_date && (
          <div className="text-light-text-tertiary dark:text-dark-text-tertiary text-xs">
            Follow-up: {new Date(lastComm.follow_up_date).toLocaleDateString()}
          </div>
        )}
      </div>
    );
  };



  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-100 dark:bg-red-900/20 border border-red-400 dark:border-red-500 text-red-700 dark:text-red-400 px-4 py-3 rounded">
          {error}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-light-bg-primary dark:bg-dark-bg-primary">
      <title>Job Applications | Talent Service</title>
      <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <h1 className="text-2xl font-bold text-light-text-primary dark:text-dark-text-primary">Job Applications</h1>
        <div className="flex flex-wrap items-center gap-3">
          {/* Activity Feed button */}
          <button
            onClick={() => setIsActivityFitOpen(true)}
            className="bg-light-bg-tertiary dark:bg-dark-bg-tertiary hover:bg-light-bg-quaternary dark:hover:bg-dark-bg-quaternary text-light-text-primary dark:text-dark-text-primary px-4 py-2 rounded-lg transition-colors flex items-center space-x-2 border border-light-border-primary dark:border-dark-border-primary"
          >
            <Activity className="w-4 h-4" />
            <span>Activity Feed</span>
          </button>

          <Link
            to="/job-applications/new"
            className="bg-blue-600 dark:bg-blue-500 hover:bg-blue-700 dark:hover:bg-blue-600 text-white px-4 py-2 rounded-lg transition-colors"
          >
            Add New Application
          </Link>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-light-bg-primary dark:bg-dark-bg-secondary rounded-lg shadow-sm border border-light-border-primary dark:border-dark-border-primary p-4 mb-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h3 className="text-lg font-medium text-light-text-primary dark:text-dark-text-primary">Filters</h3>
          <div className="flex flex-wrap items-center gap-3">
            <SavedFiltersDropdown
              pageKey={PAGE_KEYS.JOB_APPLICATIONS}
              currentFilters={filters}
              applyKeys={SAVED_FILTER_KEYS}
              onApply={(saved) => setFilters(prev => ({ ...prev, ...saved, page: 1 }))}
            />
            <button
              onClick={() => {
                const newExpanded = !filtersExpanded;
                setFiltersExpanded(newExpanded);
                localStorage.setItem('jobApplications_filtersExpanded', JSON.stringify(newExpanded));
              }}
              className="flex items-center gap-2 text-sm text-light-text-secondary dark:text-dark-text-secondary hover:text-light-text-primary dark:hover:text-dark-text-primary"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
              </svg>
              {filtersExpanded ? 'Hide' : 'Show'} Filters
            </button>
          </div>
        </div>

        {filtersExpanded && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mt-4">
          {/* Search */}
          <div>
            <label className="block text-sm font-medium text-light-text-secondary dark:text-dark-text-secondary mb-1">
              Search
            </label>
            <SearchInput
              value={filters.search}
              onChange={(value) => handleFilterChange('search', value)}
              placeholder="Search by name, contacts..."
              debounceTime={300}
            />
          </div>
          
          {/* Gender */}
          <Autocomplete
            clearable
            label="Gender"
            value={filters.gender}
            onChange={(value) => handleFilterChange('gender', value || '')}
            placeholder="Select gender..."
            options={[
              { value: 'male', label: 'Male' },
              { value: 'female', label: 'Female' },
              { value: 'other', label: 'Other' }
            ]}
          />

          {/* Status */}
          <Autocomplete
            clearable
            label="Status"
            value={filters.status_id}
            onChange={(value) => handleFilterChange('status_id', value || '')}
            placeholder="Select status..."
            options={Array.isArray(statusOptions) ? statusOptions.map((status) => ({
              value: String(status.id),
              label: status.name
            })) : []}
          />

          {/* Manager */}
          <Autocomplete
            clearable
            label="Manager"
            value={filters.manager_id}
            onChange={(value) => handleFilterChange('manager_id', value || '')}
            placeholder="Select manager..."
            options={Array.isArray(userOptions) ? userOptions.map((user) => ({
              value: String(user.id),
              label: user.name
            })) : []}
          />

          {/* Country */}
          <Autocomplete
            clearable
            label="Country"
            value={filters.country_id}
            onChange={(value) => handleFilterChange('country_id', value || '')}
            placeholder="Select country..."
            options={Array.isArray(countryOptions) ? countryOptions.map((country) => ({
              value: String(country.id),
              label: country.name
            })) : []}
          />

          {/* City */}
          <Autocomplete
            clearable
            label="City"
            value={filters.city_id}
            onChange={(value) => handleFilterChange('city_id', value || '')}
            placeholder="Select city..."
            options={Array.isArray(cityOptions) ? cityOptions.map((city) => ({
              value: String(city.id),
              label: city.name
            })) : []}
          />

          {/* Contact Tool */}
          <Autocomplete
            clearable
            label="Contact Tool"
            value={filters.tool_id}
            onChange={(value) => handleFilterChange('tool_id', value || '')}
            placeholder="Select tool..."
            options={Array.isArray(toolsOptions) ? toolsOptions.map((tool) => ({
              value: String(tool.id),
              label: tool.name
            })) : []}
          />

          {/* Link Tool */}
          <Autocomplete
            clearable
            label="Link Tool"
            value={filters.link_tool_id}
            onChange={(value) => handleFilterChange('link_tool_id', value || '')}
            placeholder="Select link tool..."
            options={Array.isArray(linkToolsOptions) ? linkToolsOptions.map((tool) => ({
              value: String(tool.id),
              label: tool.name
            })) : []}
          />

          {/* Communication Manager */}
          <Autocomplete
            clearable
            label="Communication Manager"
            value={filters.communication_manager_id}
            onChange={(value) => handleFilterChange('communication_manager_id', value || '')}
            placeholder="Select communication manager..."
            options={Array.isArray(userOptions) ? userOptions.map((user) => ({
              value: String(user.id),
              label: user.name
            })) : []}
          />

          {/* Profession */}
          <Autocomplete
            clearable
            label="Profession"
            value={filters.profession_id}
            onChange={(value) => handleFilterChange('profession_id', value || '')}
            placeholder="Select profession..."
            options={Array.isArray(professionsOptions) ? professionsOptions.map((profession) => ({
              value: String(profession.id),
              label: profession.name
            })) : []}
          />

          {/* Job Post */}
          <Autocomplete
            clearable
            label="Job Post"
            value={filters.job_post_id}
            onChange={(value) => handleFilterChange('job_post_id', value || '')}
            placeholder="Select job post..."
            options={Array.isArray(jobPostOptions) ? jobPostOptions.map((post) => ({
              value: String(post.id),
              label: post.name || `Job Post #${post.id}`
            })) : []}
          />

          {/* Department */}
          <Autocomplete
            clearable
            label="Department"
            value={filters.department_id}
            onChange={(value) => handleFilterChange('department_id', value || '')}
            placeholder="Select department..."
            options={Array.isArray(departmentsOptions) ? departmentsOptions.map((department) => ({
              value: String(department.id),
              label: department.name
            })) : []}
          />

          {/* Created Date Range */}
          <DateRangePicker
            label="Created Date Range"
            fromValue={filters.created_from}
            toValue={filters.created_to}
            onFromChange={(value) => handleFilterChange('created_from', value)}
            onToChange={(value) => handleFilterChange('created_to', value)}
            placeholder="Select date range"
          />

          {/* Updated Date Range */}
          <DateRangePicker
            label="Updated Date Range"
            fromValue={filters.updated_from}
            toValue={filters.updated_to}
            onFromChange={(value) => handleFilterChange('updated_from', value)}
            onToChange={(value) => handleFilterChange('updated_to', value)}
            placeholder="Select date range"
          />
            <div className="md:col-span-2 lg:col-span-4 flex items-center justify-end">
              <button
                type="button"
                onClick={handleResetFilters}
                className="text-sm text-light-text-secondary dark:text-dark-text-secondary hover:text-light-text-primary dark:hover:text-dark-text-primary"
              >
                Reset All Filters
              </button>
            </div>
          </div>
        )}
      </div>

      <div className="mb-4 flex min-w-0 flex-wrap items-center justify-end gap-3">
        <div className="flex w-full min-w-0 flex-wrap items-center justify-end gap-3 sm:w-auto sm:flex-1">
          {viewMode === 'cards' && (
            <CompactCardSort
              options={JOB_APPLICATION_CARD_SORT_OPTIONS}
              sortBy={filters.sort_by}
              sortOrder={filters.sort_order}
              onSortByChange={(sort_by) => setFilters(prev => ({ ...prev, sort_by, sort_order: 'ASC', page: 1 }))}
              onSortOrderChange={(sort_order) => setFilters(prev => ({ ...prev, sort_order, page: 1 }))}
            />
          )}
          <DataViewToggle value={viewMode} onChange={setViewMode} />
        </div>
      </div>

      {viewMode === 'cards' && (
        <div className="space-y-4">
          {tableLoading ? <div className="py-12 text-center text-light-text-tertiary">Loading applications…</div> : applications.length ? (
            <ResponsiveCardGrid>
              {applications.map((application) => (
                <RecordCard key={application.id}>
                  <RecordCardHeader title={<ProfileLink to={`/job-applications/${application.id}`}>{application.name || `Application #${application.id}`}</ProfileLink>} subtitle={`#${application.id}`} />
                  <RecordCardFields>
                    <RecordCardField label="Status">{renderTableCell({ key: 'status' }, application)}</RecordCardField>
                    <RecordCardField label="Manager">{application.manager?.name || userOptions.find((user) => user.id === application.manager_id)?.name || '—'}</RecordCardField>
                    <RecordCardField label="Location">{[application.city?.name, application.country?.name].filter(Boolean).join(', ') || '—'}</RecordCardField>
                    <RecordCardField label="Created">{formatDate(application.created_at)}</RecordCardField>
                    <RecordCardField label="Professions" className="sm:col-span-2">{application.professions?.map((profession) => profession.name || profession.profession_name).filter(Boolean).join(', ') || '—'}</RecordCardField>
                  </RecordCardFields>
                  <div className="mt-auto pt-4">
                    <div className="flex flex-wrap items-center justify-end gap-2 border-t border-light-border-primary pt-3 dark:border-dark-border-primary">
                      {renderTableActions(application)}
                    </div>
                  </div>
                </RecordCard>
              ))}
            </ResponsiveCardGrid>
          ) : <div className="py-12 text-center text-light-text-tertiary">No job applications found</div>}
          <div className="overflow-x-auto"><Pagination currentPage={pagination.current_page} totalPages={pagination.total_pages} totalItems={pagination.total_items} itemsPerPage={pagination.items_per_page} onPageChange={handlePageChange} showResults="range" perPageOptions={PAGINATION_OPTIONS} perPage={filters.limit} onPerPageChange={(value) => handleFilterChange('limit', parseInt(value))} showOnSinglePage hasFirstLast hasGoToPage /></div>
        </div>
      )}
      {viewMode === 'table' && <Table
        columns={tableColumns}
        data={applications}
        isLoading={tableLoading}
        sorting={sortingInfo}
        onSort={handleSort}
        rowKey={rowKey}
        stickyHeaderOffset={0}
        stickyFooterOffset={0}
        columnMenu
        {...tableColumnsState}
        footer={
          <Pagination
            currentPage={pagination.current_page}
            totalPages={pagination.total_pages}
            totalItems={pagination.total_items}
            itemsPerPage={pagination.items_per_page}
            onPageChange={handlePageChange}
            showResults="range"
            perPageOptions={PAGINATION_OPTIONS}
            perPage={filters.limit}
            onPerPageChange={(value) => handleFilterChange('limit', parseInt(value))}
            showOnSinglePage
            hasFirstLast
            hasGoToPage
          />
        }
      />}

      {/* Conversion modal */}
      <TalentFormModal
        isOpen={convertModalOpen}
        onClose={closeConvertModal}
        onSuccess={(response) => {
          console.log('Talent created successfully:', response);
          // Refresh table after successful conversion
          fetchApplications(false);
          // Show success notification
          toast.success(`Job Application successfully converted to ${selectedApplication?.talentType || 'talent'}!`, {
            duration: 3000,
            position: 'bottom-right',
          });
        }}
        initialData={{
          job_application_id: selectedApplication?.id,
          manager_id: selectedApplication?.manager_id || '',
          last_name: selectedApplication?.name?.split(' ')[0] || '',
          first_name: selectedApplication?.name?.split(' ').slice(1).join(' ') || '',
          country_id: selectedApplication?.country_id || '',
          city_id: selectedApplication?.city_id || '',
          talent_type: selectedApplication?.talentType || 'candidate',
          public_visibility: false,
          public_profile_active: false,
          professions: selectedApplication?.professions || [],
          contacts: selectedApplication?.contacts || [],
          languages: selectedApplication?.languages || [],
          cv: [],
          media: []
        }}
        jobApplicationInfo={selectedApplication}
        title="Convert Job Application to Talent"
        submitText="Convert"
      />

      {/* Status Transition Modal */}
      <StatusTransitionModal
        isOpen={showTransitionModal}
        onClose={() => {
          setShowTransitionModal(false);
          setTransitionData(null);
          setEditingStatusId(null);
          setStatusChangeLoading(null);
        }}
        entityType="job_application"
        entityId={transitionData?.talentId}
        entityName={transitionData?.talentName}
        fromStatus={transitionData?.fromStatus}
        toStatus={transitionData?.toStatus}
        onSuccess={handleTransitionSuccess}
        onError={handleTransitionError}
      />

      {/* History Modal */}
      <HistoryModal
        isOpen={historyModalOpen}
        onClose={() => setHistoryModalOpen(false)}
        talent={selectedApplicationForHistory ? { name: selectedApplicationForHistory.name } : null}
        events={selectedApplicationForHistory?.events || []}
      />

      <ActivityFitPanel
        isOpen={isActivityFitOpen}
        onClose={() => setIsActivityFitOpen(false)}
        entityType="JobApplication"
        title="Activity Feed — Job Applications"
      />

      <ConfirmDialog
        isOpen={Boolean(deleteTarget)}
        onClose={() => setDeleteTarget(null)}
        onConfirm={() => handleDelete(deleteTarget.id)}
        title="Delete Job Application"
        message={<>Are you sure you want to delete the application for <strong>{deleteTarget?.name}</strong>?</>}
        confirmText="Delete"
        confirmStyle="danger"
        isLoading={deleting}
      />
    </div>
  );
};

export default JobApplications;
